import React, { useEffect, useState } from "react";
import { Table, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useExternalCandidates } from "../../../hooks/useExternalCandidates";
import { ExternalCandidate } from "../../../services/api/externalCandidate";
import SelectDropdown from "../../../components/elements/forms/Select";
import { DropdownSelect } from "../../../components/elements/dropdown/DropdownSelect";
import { Country, State } from "country-state-city";
import { useCandidate } from "./useCandidate";
import CandidateDetailsModal from "./CandidateDetailsModal";

interface CandidateSelectionTableProps {
  jobTitle?: string;
  onSelectionChange?: (selectedIds: string[]) => void;
  externalData?: any[];
}

const CandidateSelectionTable: React.FC<CandidateSelectionTableProps> = ({
  jobTitle,
  onSelectionChange,
  externalData,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [candidateDetailsModalOpen, setCandidateDetailsModalOpen] =
    useState(false);
  const [selectedCandidateForDetails, setSelectedCandidateForDetails] =
    useState<ExternalCandidate | null>(null);

  const { candidates, pagination, loading, handlePageChange, updateFilters } =
    useExternalCandidates(1, 10);

  // Removed inline state and fetching logic as we use the hook now
  /* 
  const [candidates, setCandidates] = useState<ExternalCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 5,
    totalCount: 0,
  });
  */

  const { data, error, onChangeHandler, onChangeHandle, setData } =
    useCandidate(false);

  // Sync filters from form data + jobTitle to hook
  useEffect(() => {
    updateFilters({
      query: jobTitle,
      country: data.country,
      state: data.state,
      gender: data.gender,
      interviewRating: data.rating ? Number(data.rating) : undefined,
      cvJobMatchRating: data.cvRating ? Number(data.cvRating) : undefined,
      overallRating: data.inRating ? Number(data.inRating) : undefined,
    });
  }, [
    jobTitle,
    data.country,
    data.state,
    data.gender,
    data.rating,
    data.cvRating,
    data.inRating,
    updateFilters,
  ]);

  /*
  const fetchCandidates = async () => {
    // ... removed ...
  };

  useEffect(() => {
    fetchCandidates();
  }, [ ... ]);
  */

  /*
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({ ...prev, page, pageSize }));
  };
  */

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // Notify parent component of selection changes
    if (onSelectionChange) {
      onSelectionChange(newSelectedRowKeys.map((key) => String(key)));
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns: ColumnsType<ExternalCandidate> = [
    {
      title: "#",
      key: "index",
      width: 30,
      render: (_: any, __: any, index: number) =>
        (pagination ? (pagination.page - 1) * pagination.pageSize : 0) +
        index +
        1,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {/* <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
            <User size={16} />
          </div> */}
          <span
            className="font-medium text-gray-900 capitalize cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => {
              setSelectedCandidateForDetails(record);
              setCandidateDetailsModalOpen(true);
            }}
          >
            {record.firstName} {record.lastName}
          </span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Job Title",
      dataIndex: "professionalTitle",
      key: "professionalTitle",
      render: (text) => (
        <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
          {text}
        </span>
      ),
    },
    {
      title: "Rating",
      dataIndex: "overallRating",
      key: "overallRating",
      render: (rating) => (
        <span
          className={`px-3 py-1  rounded-full text-xs font-medium text-gray-700 ${
            rating >= 4.0
              ? "bg-green-500"
              : rating >= 3.1
                ? "bg-blue-500 text-white"
                : "bg-gray-100"
          }`}
          style={{ width: `${Math.min(rating / 5) * 100}%` }}
        >
          {rating}
        </span>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location) => (
        <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
          {location?.state}, {location?.country}
        </span>
      ),
    },
    {
      title: "Experience",
      dataIndex: "yearsOfExperience",
      key: "yearsOfExperience",
      render: (years) => (
        <div className="flex items-center gap-2">
          <div className="bg-gray-200 rounded-full h-1.5 w-10">
            <div
              className={`h-1.5 rounded-full ${
                years >= 8
                  ? "bg-green-500"
                  : years >= 4
                    ? "bg-blue-500"
                    : "bg-orange-500"
              }`}
              style={{ width: `${Math.min((years / 10) * 100, 100)}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-700">{years}yrs</span>
        </div>
      ),
      sorter: (a, b) => (a.yearsOfExperience || 0) - (b.yearsOfExperience || 0),
    },
    Table.SELECTION_COLUMN,
  ];

  /* const { data, error, onChangeHandler, onChangeHandle, setData } =
    useCandidate(false); */
  // Country/State Logic
  const countryOptions = Country.getAllCountries().map((country) => ({
    label: country?.name,
    value: country?.name,
    isoCode: country?.isoCode,
  }));

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");

  useEffect(() => {
    // Initialize selectedCountryCode if in edit mode or if country is already set
    if (data.country) {
      const country = Country.getAllCountries().find(
        (c) => c.name === data?.country,
      );
      if (country) {
        setSelectedCountryCode(country.isoCode);
      }
    }
  }, [data.country]);

  const stateOptions = selectedCountryCode
    ? State.getStatesOfCountry(selectedCountryCode).map((state) => ({
        label: state?.name,
        value: state?.name,
      }))
    : [];

  const handleCountryChange = (name: string) => {
    const country = Country.getAllCountries().find((c) => c.name === name);
    setSelectedCountryCode(country?.isoCode || "");

    // Update data with synthetic event
    onChangeHandle({
      target: {
        name: "country",
        value: name,
      },
    } as React.ChangeEvent<HTMLSelectElement>);

    // Clear state
    onChangeHandle({
      target: {
        name: "state",
        value: "",
      },
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  const handleStateChange = (name: string) => {
    onChangeHandle({
      target: {
        name: "state",
        value: name,
      },
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  // Filter logic
  const filteredCandidates = React.useMemo(() => {
    const sourceData = externalData || candidates;
    if (!sourceData) return [];

    return sourceData.filter((candidate) => {
      // Filter by Job Title (Partial Match)
      if (
        jobTitle &&
        !candidate.professionalTitle
          ?.toLowerCase()
          .includes(jobTitle.toLowerCase())
      ) {
        return false;
      }

      // Filter by Country
      if (
        data.country &&
        candidate.location?.country?.toLowerCase() !==
          data.country.toLowerCase()
      ) {
        return false;
      }

      // Filter by State
      if (
        data.state &&
        candidate.location?.state?.toLowerCase() !== data.state.toLowerCase()
      ) {
        return false;
      }

      // Filter by Gender
      if (
        data.gender &&
        data.gender !== "All" &&
        candidate.gender?.toLowerCase() !== data.gender.toLowerCase()
      ) {
        return false;
      }

      // Filter by Interview Rating (>=)
      if (
        data.rating &&
        (candidate.interviewRating === undefined ||
          candidate.interviewRating < Number(data.rating))
      ) {
        return false;
      }

      // Filter by CV Rating (>=)
      if (
        data.cvRating &&
        (candidate.cvJobMatchRating === undefined ||
          candidate.cvJobMatchRating < Number(data.cvRating))
      ) {
        return false;
      }

      // Filter by 25th Rating (Overall) (>=)
      if (
        data.inRating &&
        (candidate.overallRating === undefined ||
          candidate.overallRating < Number(data.inRating))
      ) {
        return false;
      }

      return true;
    });
  }, [
    externalData,
    candidates,
    jobTitle,
    data.country,
    data.state,
    data.gender,
    data.rating,
    data.cvRating,
    data.inRating,
  ]);

  return (
    <div className="font-abel">
      <div>
        <p className="text-15 font-semibold mb-2">Filter</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <div className="col-span-1">
            <label className="text-15 text-[#1E1E1E] block text-borderBColor capitalize mb-1">
              Country
            </label>
            <DropdownSelect
              options={countryOptions}
              // name="country"
              placeholder="Select Country"
              value={data.country || ""}
              onChange={handleCountryChange}
              searchable
            />
          </div>

          <div className="col-span-1">
            <label className="text-15 text-[#1E1E1E] block text-borderBColor capitalize mb-1">
              State/Region
            </label>
            <DropdownSelect
              options={stateOptions}
              placeholder="Select State/Region"
              value={data.state || ""}
              onChange={handleStateChange}
              searchable
              disabled={!selectedCountryCode}
            />
          </div>
          <div className="col-span-1">
            <SelectDropdown
              items={[
                { id: "All", name: "All" },
                { id: "Male", name: "Male" },
                { id: "Female", name: "Female" },
                { id: "Other", name: "Other" },
              ]}
              name="gender"
              label="Gender"
              value={data.gender || ""}
              onChange={onChangeHandle}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <div className="col-span-1">
            <SelectDropdown
              items={[
                { id: 1, name: "1" },
                { id: 2, name: "2" },
                { id: 3, name: "3" },
                { id: 4, name: "4" },
                { id: 5, name: "5" },
              ]}
              name="rating"
              label="Interview Rating"
              value={data.rating || ""}
              onChange={onChangeHandle}
            />
          </div>
          <div className="col-span-1">
            <SelectDropdown
              items={[
                { id: 1, name: "1" },
                { id: 2, name: "2" },
                { id: 3, name: "3" },
                { id: 4, name: "4" },
                { id: 5, name: "5" },
              ]}
              name="cvRating"
              label="CV Rating"
              value={data.cvRating || ""}
              onChange={onChangeHandle}
            />
          </div>

          <div className="col-span-1">
            <SelectDropdown
              items={[
                { id: 1, name: "1" },
                { id: 2, name: "2" },
                { id: 3, name: "3" },
                { id: 4, name: "4" },
                { id: 5, name: "5" },
              ]}
              name="inRating"
              label="25th Rating"
              value={data.inRating || ""}
              onChange={onChangeHandle}
            />
          </div>
        </div>
      </div>
      <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden relative">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          // dataSource={candidates}
          dataSource={filteredCandidates}
          rowKey={(record) => record.id || record.email}
          loading={!externalData && loading}
          pagination={
            externalData
              ? { pageSize: 10 }
              : {
                  current: pagination?.page || 1,
                  pageSize: pagination?.pageSize || 10,
                  total: pagination?.totalCount || 0,
                  onChange: handlePageChange,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                }
          }
          title={() => (
            <div className="px-2 font-abel">
              <h3 className="text-base font-semibold text-gray-900">
                Select Candidates
              </h3>
              <p className="text-sm text-gray-500">
                Select candidates from external sources to include in this
                process
              </p>
            </div>
          )}
        />
      </div>

      {/* Candidate Details Modal */}
      <CandidateDetailsModal
        isOpen={candidateDetailsModalOpen}
        onClose={() => {
          setCandidateDetailsModalOpen(false);
          setSelectedCandidateForDetails(null);
        }}
        candidate={selectedCandidateForDetails}
      />
    </div>
  );
};

export default CandidateSelectionTable;
